import React, { useState, useEffect, useRef } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import {
    getAnonymousExamRecordByBarcode,
    updateAnonymousExamRecordNote,
    // Note: Assure-toi que cette action existe dans ton store
    resetEvaluationLastActionSuccess as OnResetEvaluationLastActionSuccess,
} from "store/actions";
import { 
    Col, 
    Row, 
    Card, 
    CardBody, 
    Form, 
    FormGroup, 
    Label, 
    Input, 
    Button,
    Alert,
    Badge
} from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer, toast } from "react-toastify";

import "./notation.css"; // 👈 Ajout ici (le ./ signifie "ce dossier")

const NotationSimple = () => {
    document.title = "Notation Rapide | CinzanEdu";

    const dispatch = useDispatch();
    const barcodeInputRef = useRef(null);
    const noteInputRef = useRef(null); // 🆕 Pour focus sur la note après recherche
    
    const [barcode, setBarcode] = useState("");
    const [note, setNote] = useState("");
    const [searchTriggered, setSearchTriggered] = useState(false);

    // --- SELECTOR (Corrigé et déplacé au top niveau) ---
    const selectAnonymousData = createSelector(
        (state) => state.anonymousExamRecordReducer, 
        (state) => ({
            currentRecord: state.currentRecord,
            loading: state.loading,
            lastActionSuccess: state.lastActionSuccess,
            error: state.error
        })
    );

    const { currentRecord, loading, lastActionSuccess, error } = useSelector(selectAnonymousData);

    // --- AUTO FOCUS INITIAL ---
    useEffect(() => {
        if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
        }
    }, []);

    // --- GESTION DU FOCUS APRÈS RECHERCHE ---
    useEffect(() => {
        if (currentRecord && searchTriggered) {
            setNote(currentRecord.note?.value || "");
            setSearchTriggered(false);
            // Focus automatique sur le champ note dès qu'on trouve l'étudiant
            setTimeout(() => {
                if (noteInputRef.current) noteInputRef.current.focus();
            }, 100);
        }
    }, [currentRecord, searchTriggered]);

    // --- RESET APRÈS SUCCÈS ---
    useEffect(() => {
        if (lastActionSuccess) {
            toast.success("Note enregistrée avec succès !");
            handleReset();
            if (OnResetEvaluationLastActionSuccess) {
                dispatch(OnResetEvaluationLastActionSuccess());
            }
        }
    }, [lastActionSuccess, dispatch]);

    // --- GESTION ERREUR ---
    useEffect(() => {
        if (error && searchTriggered) {
            toast.error(error);
            setSearchTriggered(false);
        }
    }, [error, searchTriggered]);

    // --- RECHERCHE ---
    const handleSearch = (e) => {
        e?.preventDefault();
        if (!barcode.trim()) {
            toast.warning("Veuillez saisir un code-barre");
            return;
        }
        setSearchTriggered(true);
        dispatch(getAnonymousExamRecordByBarcode(barcode.trim()));
    };

    const handleBarcodeKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch(e);
        }
    };

    // --- VALIDATION ET ENVOI ---
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!currentRecord) return;

        if (note === "") {
            toast.warning("Veuillez saisir une note");
            return;
        }

        const noteValue = parseFloat(note);
        if (isNaN(noteValue) || noteValue < 0 || noteValue > 20) {
            toast.error("La note doit être entre 0 et 20");
            return;
        }

        dispatch(updateAnonymousExamRecordNote({
            uuid: currentRecord.uuid,
            note_value: noteValue
        }));
    };

    // --- RESET ---
    const handleReset = () => {
        setBarcode("");
        setNote("");
        setSearchTriggered(false);
        if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs title="Notation" breadcrumbItem="Saisie Rapide des Notes" />

                    {/* Correction Prop Spinner */}
                    {loading && <Spinners setLoading={() => {}} />} 

                    <Row className="justify-content-center">
                        <Col lg="8" xl="6">
                            <Card>
                                <CardBody>
                                    <h5 className="card-title mb-4 text-primary">
                                        <i className="bx bx-barcode-reader me-2"></i>
                                        Scan du Code-barre
                                    </h5>

                                    <Form onSubmit={handleSearch}>
                                        <FormGroup>
                                            <div className="d-flex gap-2">
                                                <Input
                                                    type="text"
                                                    innerRef={barcodeInputRef}
                                                    value={barcode}
                                                    onChange={(e) => setBarcode(e.target.value)}
                                                    onKeyPress={handleBarcodeKeyPress}
                                                    placeholder="Scannez ici..."
                                                    disabled={loading}
                                                    className="form-control-lg bg-light fw-bold text-center"
                                                    style={{ letterSpacing: "2px" }}
                                                />
                                                <Button 
                                                    color="primary" 
                                                    type="submit"
                                                    disabled={loading || !barcode.trim()}
                                                >
                                                    <i className="bx bx-search-alt"></i>
                                                </Button>
                                            </div>
                                        </FormGroup>
                                    </Form>

                                    {currentRecord && (
                                        <div className="mt-4 animate__animated animate__fadeIn">
                                            <Alert color="info" className="border-0 shadow-sm">
                                                <Row>
                                                    <Col xs="12" className="mb-2">
                                                        <Badge color="primary" className="mb-2">Info Étudiant</Badge>
                                                        <div className="fw-bold fs-5 text-uppercase">{currentRecord.barcode}</div>
                                                    </Col>
                                                    <Col xs="6" className="small">
                                                        <strong>EC :</strong> {currentRecord.course_component?.title}<br/>
                                                        <strong>Eval :</strong> {currentRecord.evaluation?.title}
                                                    </Col>
                                                    <Col xs="6" className="small text-end text-muted">
                                                        {currentRecord.study_level?.name}<br/>
                                                        {currentRecord.semester?.code}
                                                    </Col>
                                                </Row>
                                            </Alert>

                                            <Form onSubmit={handleSubmit} className="mt-4">
                                                <FormGroup>
                                                    <Label className="fw-bold">Note Finale (0-20)</Label>
                                                    <Input
                                                        type="number"
                                                        innerRef={noteInputRef}
                                                        value={note}
                                                        onChange={(e) => setNote(e.target.value)}
                                                        placeholder="0.00"
                                                        min="0"
                                                        max="20"
                                                        step="0.25"
                                                        className="form-control-lg text-center fs-3 fw-bold border-primary"
                                                        disabled={loading}
                                                    />
                                                </FormGroup>

                                                <div className="d-flex gap-2 mt-4">
                                                    <Button color="light" type="button" className="flex-grow-1" onClick={handleReset}>
                                                        Annuler
                                                    </Button>
                                                    <Button color="success" type="submit" className="flex-grow-1" disabled={loading || note === ""}>
                                                        <i className="bx bx-save me-1"></i> Valider la note
                                                    </Button>
                                                </div>
                                            </Form>
                                        </div>
                                    )}

                                    {!currentRecord && searchTriggered && !loading && (
                                        <Alert color="danger" className="mt-4">
                                            ⚠️ Code-barre non reconnu
                                        </Alert>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default NotationSimple;